import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  MdPeople, MdFavorite, MdTrendingUp, MdTrendingDown,
  MdAccountBalance, MdCalendarMonth, MdRefresh, MdArrowUpward,
  MdArrowDownward, MdFlashOn, MdBarChart, MdDonutLarge,
  MdShowChart, MdWarning, MdCheckCircle, MdToday, MdAutoAwesome
} from "react-icons/md";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ─── Helpers ─── */
const fmt = (n) =>
  n >= 100000 ? `₹${(n / 100000).toFixed(1)}L`
  : n >= 1000  ? `₹${(n / 1000).toFixed(1)}K`
  : `₹${Number(n).toLocaleString("en-IN")}`;
const fmtFull = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const timeAgo = (d, t) => {
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return t("dbJustNow");
  if (m < 60) return t("dbMinsAgo", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("dbHoursAgo", { count: h });
  return t("dbDaysAgo", { count: Math.floor(h / 24) });
};

/* ─── Live Clock ─── */
function LiveClock() {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(new Date());
  useEffect(() => { const tId = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(tId); }, []);
  
  const dateStr = now.toLocaleDateString(i18n.language === "ml" ? "ml-IN" : "en-IN", {
    weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'
  });

  return (
    <div className="db-clock">
      <div className="db-clock-time">
        {String(now.getHours()).padStart(2,"0")}:{String(now.getMinutes()).padStart(2,"0")}
        <span className="db-clock-sec">:{String(now.getSeconds()).padStart(2,"0")}</span>
      </div>
      <div className="db-clock-date">{dateStr}</div>
    </div>
  );
}

/* ─── Sparkline ─── */
function Sparkline({ data, color = "#c9973a", h = 36, w = 90 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data); const min = Math.min(...data); const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * (h - 4) - 2}`);
  return (
    <svg width={w} height={h} style={{ display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={`sg${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M${pts[0]} ${pts.slice(1).map(p=>`L${p}`).join(" ")} L${w},${h} L0,${h} Z`} fill={`url(#sg${color.replace("#","")})`} />
      <path d={`M${pts.join(" L")}`} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Bar Chart ─── */
function BarChart({ data, height = 130 }) {
  const { t } = useTranslation();
  if (!data || !data.length) return null;
  const maxVal = Math.max(...data.map(d => Math.max(d.income, d.expense)), 1);
  const bW = 14, gap = 5, gW = bW * 2 + gap + 12;
  const W = data.length * gW;
  return (
    <div style={{ overflowX: "auto", paddingBottom: 4 }}>
      <svg width={Math.max(W, 300)} height={height + 30}>
        <defs>
          <linearGradient id="gi" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4A017"/><stop offset="100%" stopColor="#8B5E00"/></linearGradient>
          <linearGradient id="ge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C0392B"/><stop offset="100%" stopColor="#8B0000"/></linearGradient>
        </defs>
        {[0,.25,.5,.75,1].map((f,i) => <line key={i} x1={0} y1={height*(1-f)} x2={W} y2={height*(1-f)} stroke="rgba(212,160,23,0.1)" strokeWidth="1"/>)}
        {data.map((d, i) => {
          const x = i * gW + 5;
          const iH = (d.income / maxVal) * (height - 8);
          const eH = (d.expense / maxVal) * (height - 8);
          return (
            <g key={i}>
              <rect x={x} y={height - iH} width={bW} height={iH} rx="3" fill="url(#gi)" opacity="0.9"/>
              <rect x={x + bW + gap} y={height - eH} width={bW} height={eH} rx="3" fill="url(#ge)" opacity="0.9"/>
              <text x={x + bW + gap/2} y={height + 18} textAnchor="middle" style={{ fontFamily:"'Cinzel',serif", fontSize:"7px", fill:"#9B6A3A" }}>{d.label}</text>
            </g>
          );
        })}
      </svg>
      <div style={{ display:"flex", gap:14, marginTop:6, paddingLeft:4 }}>
        {[["#D4A017", t("income")], ["#C0392B", t("expense")]].map(([c,l]) => (
          <div key={l} style={{ display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ width:10,height:10,borderRadius:2,background:c,display:"inline-block" }}/>
            <span style={{ fontFamily:"'Cinzel',serif", fontSize:"9px", color:"#9B6A3A", letterSpacing:1 }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Donut ─── */
function DonutChart({ segments, size = 110, thickness = 20 }) {
  const { t } = useTranslation();
  const r = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  let off = 0;
  return (
    <svg width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(212,160,23,0.1)" strokeWidth={thickness}/>
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * circ, gap = circ - dash;
        const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color} strokeWidth={thickness}
          strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-off} strokeLinecap="butt"
          style={{ transform:"rotate(-90deg)", transformOrigin:"center" }}/>;
        off += dash; return el;
      })}
      <text x={cx} y={cy-4} textAnchor="middle" style={{ fontFamily:"'Cinzel Decorative',cursive", fontSize:"13px", fontWeight:900, fill:"#D4A017" }}>{segments.length}</text>
      <text x={cx} y={cy+12} textAnchor="middle" style={{ fontFamily:"'Cinzel',serif", fontSize:"7px", fill:"#9B6A3A", letterSpacing:1 }}>{t("dbTypes")}</text>
    </svg>
);
}

/* ─── HBar ─── */
function HBar({ label, value, max, color = "#c9973a", amount }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="db-hbar-row">
      <div className="db-hbar-top">
        <span className="db-hbar-label">{label}</span>
        <span className="db-hbar-val">{value} <span className="db-hbar-amt">{fmtFull(amount)}</span></span>
      </div>
      <div className="db-hbar-track"><div className="db-hbar-fill" style={{ width:`${pct}%`, background:color }}/></div>
    </div>
  );
}

/* ─── Stat Card ─── */
function StatCard({ icon: Icon, label, value, sub, subPositive, sparkData, color, delay = 0 }) {
  return (
    <div className="db-stat-card" style={{ "--sc-color": color, animationDelay:`${delay}s` }}>
      <div className="db-sc-top">
        <div className="db-sc-icon-wrap"><Icon size={20}/><div className="db-sc-icon-ring"/></div>
        <div className="db-sc-spark"><Sparkline data={sparkData} color={color}/></div>
      </div>
      <div className="db-sc-value">{value}</div>
      <div className="db-sc-label">{label}</div>
      {sub && (
        <div className={`db-sc-sub ${subPositive ? "pos" : "neg"}`}>
          {subPositive ? <MdArrowUpward size={10}/> : <MdArrowDownward size={10}/>}{sub}
        </div>
      )}
      <div className="db-sc-glow"/>
    </div>
  );
}

/* ─── Quick Action Card ─── */
function QuickAction({ icon: Icon, label, color, onClick }) {
  return (
    <button className="db-qa-btn" onClick={onClick} style={{ "--qa-color": color }}>
      <div className="db-qa-icon"><Icon size={18}/></div>
      <span className="db-qa-label">{label}</span>
    </button>
  );
}

/* ─── Cash vs Online Bar ─── */
function PaymentModeBar({ data }) {
  const { t } = useTranslation();
  const cash = data.find(d => d.payment_mode === "cash") || { total: 0, count: 0 };
  const online = data.find(d => d.payment_mode === "razorpay") || { total: 0, count: 0 };
  const total = +cash.total + +online.total || 1;
  const cashPct = ((+cash.total / total) * 100).toFixed(1);
  const onlinePct = ((+online.total / total) * 100).toFixed(1);
  return (
    <div className="db-pmode">
      <div className="db-pmode-bar">
        <div className="db-pmode-seg cash" style={{ width:`${cashPct}%` }}/>
        <div className="db-pmode-seg online" style={{ width:`${onlinePct}%` }}/>
      </div>
      <div className="db-pmode-legend">
        <div className="db-pmode-item"><span className="db-pmode-dot cash"/><div><div className="db-pmode-lbl">{t("dbCash")}</div><div className="db-pmode-amt">{fmtFull(cash.total)} ({cash.count} {t("dbTxn")})</div></div></div>
        <div className="db-pmode-item"><span className="db-pmode-dot online"/><div><div className="db-pmode-lbl">{t("dbOnlineRazorpay")}</div><div className="db-pmode-amt">{fmtFull(online.total)} ({online.count} {t("dbTxn")})</div></div></div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════ */
export default function DashboardPage({ onNavigate }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetch$ = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/dashboard`);
      if (!res.ok) throw new Error(t("failedToLoadDashboardData"));
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { fetch$(); }, [fetch$]);

  if (loading) return <DashboardLoader />;
  if (error)   return <DashboardError message={error} onRetry={() => fetch$()} />;
  if (!data)   return null;

  const {
    summary = {},
    monthlyTrend = [],
    topCategories = [],
    donationBreakdown = [],
    expenseBreakdown = [],
    recentActivity = [],
    today = { todayIncome: 0, todayBookings: 0, todayDonations: 0 },
    pendingCount = 0,
    newDevoteesThisWeek = 0,
    cashVsOnline = [],
  } = data;
  const incomeSpark  = monthlyTrend.map(m => +m.income);
  const expenseSpark = monthlyTrend.map(m => +m.expense);
  const maxCat       = Math.max(...topCategories.map(c => +c.bookingCount), 1);
  const donutColors  = ["#D4A017","#8B0000","#1A5C2A","#C04A00","#5C3A1E"];
  const donutSegs    = donationBreakdown.map((d, i) => ({ label:d.type, value:+d.total||1, color:donutColors[i%donutColors.length] }));

  return (
    <>
      <DashboardStyles />
      <div className="db-root">

        {/* ── HEADER ── */}
        <div className="db-header">
          <div className="db-header-banner">
            <div className="db-hb-glass" />
            <div className="db-hb-pattern" />
            
            <div className="db-hb-rings">
              {[68, 52, 38].map((s, i) => (
                <div key={i} className="db-hb-ring" style={{ width: s, height: s, animationDelay: `${i * 0.4}s` }} />
              ))}
              <span className="db-hb-om">ॐ</span>
              <div className="db-hb-glow" />
            </div>

            <div className="db-hb-text">
              <div className="db-hb-welcome">
                <span className="db-hb-eyebrow">{t("welcome")}</span>
                <h1 className="db-hb-temple">Administrator</h1>
              </div>
              <div className="db-hb-temple-name">{t("dbAdminDashboard")}</div>
              <div className="db-hb-sub">{t("dbTempleNameDetail")} · {t("dbTempleLocDetail")}</div>
            </div>

            <div className="db-header-right">
              <LiveClock t={t} />
              <button 
                className={`db-refresh-banner-btn ${refreshing ? "spinning" : ""}`} 
                onClick={() => fetch$(true)} 
                disabled={refreshing}
              >
                <MdRefresh size={18} className={refreshing ? "spinning" : ""}/>
                <span>{refreshing ? t("dbRefreshing") : t("dbRefresh")}</span>
              </button>
            </div>
          </div>
          {lastUpdated && <div className="db-updated-bar">✦ {t("dbLastUpdated")}: {lastUpdated.toLocaleTimeString()}</div>}
        </div>

        {/* ── ALERT: Pending Members ── */}
        {pendingCount > 0 && (
          <div className="db-alert-banner" onClick={() => onNavigate?.("devotees")}>
            <MdWarning size={18}/>
            <span>{t("dbAwaitingApprovalCount", { count: pendingCount })}.</span>
            <span className="db-alert-action">{t("dbReviewNow")} ›</span>
          </div>
        )}

        {/* ── TODAY'S QUICK STATS ── */}
        <div className="db-today-row">
          <div className="db-today-card gold">
            <MdToday size={16}/>
            <div><div className="db-today-val">{fmtFull(today.todayIncome)}</div><div className="db-today-lbl">{t("dbTodayRevenue")}</div></div>
          </div>
          <div className="db-today-card crimson">
            <MdCalendarMonth size={16}/>
            <div><div className="db-today-val">{today.todayBookings}</div><div className="db-today-lbl">{t("dbBookingsToday")}</div></div>
          </div>
          <div className="db-today-card green">
            <MdFavorite size={16}/>
            <div><div className="db-today-val">{today.todayDonations}</div><div className="db-today-lbl">{t("dbDonationsToday")}</div></div>
          </div>
          <div className="db-today-card purple">
            <MdPeople size={16}/>
            <div><div className="db-today-val">{newDevoteesThisWeek}</div><div className="db-today-lbl">{t("dbNewThisWeek")}</div></div>
          </div>
        </div>

        {/* ── QUICK ACTIONS ── */}
        <div className="db-section-label">⚡ {t("dbQuickActions")}</div>
        <div className="db-qa-row">
          <QuickAction icon={MdPeople} label={t("dbManageMembers")} color="#D4A017" onClick={() => onNavigate?.("devotees")}/>
          <QuickAction icon={MdCalendarMonth} label={t("dbNewBooking")} color="#8B5E00" onClick={() => onNavigate?.("bookings")}/>
          <QuickAction icon={MdFavorite} label={t("dbNewDonation")} color="#C04A00" onClick={() => onNavigate?.("donations")}/>
          <QuickAction icon={MdTrendingDown} label={t("dbAddExpense")} color="#8B0000" onClick={() => onNavigate?.("expense")}/>
          <QuickAction icon={MdBarChart} label={t("dbViewReports")} color="#1A5C2A" onClick={() => onNavigate?.("reports")}/>
          <QuickAction icon={MdAutoAwesome} label={t("dbSpecial")} color="#5C3A1E" onClick={() => onNavigate?.("calendar")}/>
        </div>

        {/* ── STAT GRID ── */}
        <div className="db-stats-grid">
          <StatCard icon={MdPeople} label={t("dbTotalDevotees")} value={summary.totalDevotees} sub={`+${summary.newDevoteesThisMonth} ${t("dbThisMonth")}`} subPositive sparkData={[0,summary.approvedDevotees*0.3,summary.approvedDevotees*0.6,summary.approvedDevotees*0.8,+summary.approvedDevotees]} color="#D4A017" delay={0}/>
          <StatCard icon={MdTrendingUp} label={t("dbTotalIncome")} value={fmt(summary.totalIncome)} sub={`${fmt(+summary.bookingIncomeThisMonth + +summary.donationIncomeThisMonth)} ${t("dbThisMonth")}`} subPositive sparkData={incomeSpark} color="#1A5C2A" delay={0.07}/>
          <StatCard icon={MdCalendarMonth} label={t("dbBookings")} value={summary.totalBookings} sub={`${summary.bookingsThisMonth} ${t("dbThisMonth")}`} subPositive sparkData={[...monthlyTrend.map(m=>+m.income/1000)]} color="#8B5E00" delay={0.14}/>
          <StatCard icon={MdFavorite} label={t("dbDonations")} value={summary.totalDonations} sub={`${summary.donationsThisMonth} ${t("dbThisMonth")}`} subPositive sparkData={donationBreakdown.map(d=>+d.total)} color="#C04A00" delay={0.21}/>
          <StatCard icon={MdTrendingDown} label={t("dbExpenses")} value={fmt(summary.totalExpenseAmount)} sub={`${fmt(summary.expenseThisMonth)} ${t("dbThisMonth")}`} subPositive={false} sparkData={expenseSpark} color="#8B0000" delay={0.28}/>
          <StatCard icon={MdAccountBalance} label={t("dbNetBalance")} value={fmt(Math.abs(summary.netBalance))} sub={+summary.netBalance >= 0 ? t("dbSurplus") : t("dbDeficit")} subPositive={+summary.netBalance >= 0} sparkData={monthlyTrend.map(m=>+m.net)} color={+summary.netBalance >= 0 ? "#1A5C2A":"#8B0000"} delay={0.35}/>
        </div>

        {/* ── CHART + PULSE ── */}
        <div className="db-mid-grid">
          <div className="db-panel db-panel-chart">
            <div className="db-panel-header"><div className="db-panel-icon"><MdShowChart size={14}/></div><div><div className="db-panel-title">{t("dbIncomeVsExpense")}</div><div className="db-panel-sub">{t("dbLast6MonthsTrend")}</div></div></div>
            <div className="db-panel-rule"/>
            <div className="db-chart-summary-row">
              {[{label:t("dbTotalIncome"),val:fmtFull(summary.totalIncome),color:"#c9973a"},{label:t("dbExpenses"),val:fmtFull(summary.totalExpenseAmount),color:"#8b1a1a"},{label:t("dbNetBalance"),val:fmtFull(Math.abs(summary.netBalance)),color:+summary.netBalance>=0?"#2a6b2a":"#8b1a1a"}].map((s,i)=>(
                <div key={i} className="db-chart-sum-item"><span className="db-csi-dot" style={{ background:s.color }}/><div><div className="db-csi-label">{s.label}</div><div className="db-csi-val" style={{ color:s.color }}>{s.val}</div></div></div>
              ))}
            </div>
            <div className="db-bar-wrap"><BarChart data={monthlyTrend} height={130}/></div>
          </div>

          <div className="db-panel">
            <div className="db-panel-header"><div className="db-panel-icon"><MdFlashOn size={14}/></div><div><div className="db-panel-title">{t("dbTemplePulse")}</div><div className="db-panel-sub">{t("dbAtAGlance")}</div></div></div>
            <div className="db-panel-rule"/>
            <div className="db-pulse-grid">
              {[
                {label:t("dbApproved"),val:summary.approvedDevotees,color:"#1A5C2A"},
                {label:t("dbPending"),val:summary.pendingDevotees,color:"#D4A017"},
                {label:t("dbBookingsMo"),val:summary.bookingsThisMonth,color:"#8B5E00"},
                {label:t("dbDonationsMo"),val:summary.donationsThisMonth,color:"#C04A00"},
              ].map((p,i)=>(
                <div key={i} className="db-pulse-item" style={{ "--pi-color":p.color }}>
                  <div className="db-pi-val" style={{ color:p.color }}>{p.val}</div>
                  <div className="db-pi-label">{p.label}</div>
                </div>
              ))}
            </div>
            <div className="db-panel-rule" style={{ margin:"0 0 14px" }}/>
            <div className="db-is-wrap">
              <div className="db-is-title">{t("dbIncomeSplit")}</div>
              {[[t("dbOfferings"),summary.bookingIncomeThisMonth,"#c9973a"],[t("dbDonations"),summary.donationIncomeThisMonth,"#b5580a"]].map(([lbl,amt,col])=>{
                const total = (+summary.bookingIncomeThisMonth + +summary.donationIncomeThisMonth) || 1;
                return (
                  <div key={lbl} className="db-is-row">
                    <span className="db-is-type">{lbl}</span>
                    <span className="db-is-bar-wrap"><span className="db-is-bar" style={{ width:`${(amt/total)*100}%`, background:col }}/></span>
                    <span className="db-is-amt">{fmtFull(amt)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── PAYMENT MODE BREAKDOWN ── */}
        <div className="db-panel">
          <div className="db-panel-header"><div className="db-panel-icon"><MdAccountBalance size={14}/></div><div><div className="db-panel-title">{t("dbPaymentChannels")}</div><div className="db-panel-sub">{t("dbCashVsOnline")}</div></div></div>
          <div className="db-panel-rule"/>
          <div style={{ padding:"0 22px 20px" }}><PaymentModeBar data={cashVsOnline}/></div>
        </div>

        {/* ── BOTTOM GRID ── */}
        <div className="db-bot-grid">
          <div className="db-panel">
            <div className="db-panel-header"><div className="db-panel-icon"><MdBarChart size={14}/></div><div><div className="db-panel-title">{t("dbTopOfferings")}</div><div className="db-panel-sub">{t("dbByBookingCount")}</div></div></div>
            <div className="db-panel-rule"/>
            <div className="db-hbars">
              {topCategories.length === 0 ? <div className="db-empty-msg">{t("dbNoBookings")}</div> :
                topCategories.map((cat,i) => <HBar key={i} label={cat.name} value={+cat.bookingCount} max={maxCat} color={["#D4A017","#8B5E00","#1A5C2A","#C04A00","#8B0000","#5C3A1E"][i%6]} amount={cat.revenue}/>)}
            </div>
          </div>

          <div className="db-panel">
            <div className="db-panel-header"><div className="db-panel-icon"><MdDonutLarge size={14}/></div><div><div className="db-panel-title">{t("dbDonationTypes")}</div><div className="db-panel-sub">{t("dbRevenueByType")}</div></div></div>
            <div className="db-panel-rule"/>
            {donationBreakdown.length === 0 ? <div className="db-empty-msg">{t("dbNoDonations")}</div> : (
              <div className="db-donut-wrap">
                <DonutChart segments={donutSegs} size={110} thickness={20}/>
                <div className="db-donut-legend">
                  {donationBreakdown.map((d,i) => (
                    <div key={i} className="db-dl-item">
                      <span className="db-dl-dot" style={{ background:donutColors[i%donutColors.length] }}/>
                      <span className="db-dl-name">{d.type}</span>
                      <span className="db-dl-val">{fmtFull(d.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="db-panel">
            <div className="db-panel-header"><div className="db-panel-icon"><MdTrendingDown size={14}/></div><div><div className="db-panel-title">{t("dbExpenseCategories")}</div><div className="db-panel-sub">{t("dbByTotalAmount")}</div></div></div>
            <div className="db-panel-rule"/>
            <div className="db-hbars">
              {expenseBreakdown.length === 0 ? <div className="db-empty-msg">{t("dbNoExpenses")}</div> :
                expenseBreakdown.map((ex,i) => <HBar key={i} label={ex.title} value={+ex.count} max={Math.max(...expenseBreakdown.map(e=>+e.count),1)} color={["#8B0000","#A93226","#C0392B","#8B1A1A","#6B0000","#4A0000"][i%6]} amount={ex.total}/>)}
            </div>
          </div>
        </div>

        {/* ── RECENT ACTIVITY ── */}
        <div className="db-panel db-activity-panel">
          <div className="db-panel-header"><div className="db-panel-icon"><MdFlashOn size={14}/></div><div><div className="db-panel-title">{t("dbRecentActivity")}</div><div className="db-panel-sub">{t("dbLatestTransactions")}</div></div></div>
          <div className="db-panel-rule"/>
          {recentActivity.length === 0 ? <div className="db-empty-msg" style={{ padding:"24px 0" }}>{t("dbNoRecentActivity")}</div> : (
            <div className="db-activity-list">
              {recentActivity.map((act,i) => (
                <div key={i} className="db-act-row" style={{ animationDelay:`${i*0.06}s` }}>
                  <div className={`db-act-badge ${act.type}`}>
                    {act.type === "booking" ? <MdCalendarMonth size={13}/> : <MdFavorite size={13}/>}
                  </div>
                  <div className="db-act-info">
                    <span className="db-act-label">{act.label || act.type}</span>
                    <span className="db-act-ref">{act.ref}</span>
                  </div>
                  <div className="db-act-right">
                    <span className="db-act-amount">{fmtFull(act.amount)}</span>
                    <span className="db-act-time">{timeAgo(act.created_at, t)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      <DashboardStyles />
    </>
  );
}

/* ─── Loading ─── */
function DashboardLoader() {
  const { t } = useTranslation();
  return (
    <div className="db-loader-wrap">
      <DashboardStyles />
      <div className="db-loader-ring-wrap">
        <div className="db-loader-ring" />
        <div className="db-loader-ring-inner" />
        <div className="db-loader-om">ॐ</div>
      </div>
      <p className="db-loader-text">{t("dbLoading")}</p>
    </div>
  );
}

/* ─── Error ─── */
function DashboardError({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="db-error-wrap">
      <DashboardStyles />
      <MdWarning size={48} style={{ color:"#8B0000", opacity:0.5 }}/>
      <p className="db-error-msg">{message}</p>
      <button className="db-refresh-btn" onClick={onRetry}>{t("dbRetry")}</button>
    </div>
  );
}

/* ─── Styles ─── */
function DashboardStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');
      :root {
        --parchment: #FBF0DC; --panel: #FFF8EE; --text-dark: #3B1A08; --text-mid: #6B3A18; --text-muted: #9B6A3A;
        --gold-bright: #F0C040; --gold-mid: #D4A017; --gold-dim: #8B5E00;
        --crimson: #8B0000; --crimson-mid: #A93226; --green: #1A5C2A;
      }
      *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
      .db-root { padding:24px 32px 56px; background:var(--parchment); min-height:100vh; display:flex; flex-direction:column; gap:20px; animation:dbFadeIn 0.45s ease both; }
      @keyframes dbFadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }

      /* HEADER */
      .db-header { display:flex; flex-direction:column; gap:12px; }
      .db-header-banner { position:relative; overflow:hidden; display:flex; align-items:center; gap:26px; border-radius:18px; padding:28px 36px;
        background: linear-gradient(135deg,#3D0A0A 0%,#5A1010 40%,#3A0808 70%,#2A0505 100%);
        border:1px solid rgba(212,160,23,0.35);
        box-shadow:0 4px 6px rgba(59,26,8,0.08),0 12px 40px rgba(59,26,8,0.22),inset 0 1px 0 rgba(255,220,100,0.12);
        transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
      }
      .db-hb-glass { position:absolute; inset:0; background:radial-gradient(ellipse at 20% 50%, rgba(139,0,0,0.45) 0%, transparent 55%); pointer-events:none; }
      .db-hb-pattern { position:absolute; inset:0; opacity:0.04; pointer-events:none;
        background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l30 30-30 30L0 30z' fill='%23D4A017' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E");
      }
      .db-header-banner::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; border-radius:18px 18px 0 0;
        background:linear-gradient(90deg,transparent 5%,#8B0000 15%,#D4A017 35%,#F0C040 50%,#D4A017 65%,#8B0000 85%,transparent 95%); z-index:2; }
      
      .db-hb-rings { position:relative; width:80px; height:80px; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
      .db-hb-ring { position:absolute; border-radius:50%; border:1px solid rgba(212,160,23,0.3); animation:hbSpin 12s linear infinite; }
      .db-hb-ring:nth-child(2) { animation-duration:18s; animation-direction:reverse; border-style:dashed; opacity:0.6; }
      .db-hb-ring:nth-child(3) { animation-duration:8s; border-style:dotted; opacity:0.8; }
      @keyframes hbSpin { to { transform:rotate(360deg); } }
      .db-hb-om { font-family:serif; font-size:32px; color:#F0C040; text-shadow:0 0 20px rgba(240,192,64,0.7); animation:omPulse 3s ease-in-out infinite; position:relative; z-index:1; }
      @keyframes omPulse { 0%,100% { opacity:0.75; transform:scale(1); } 50% { opacity:1; transform:scale(1.05); } }
      .db-hb-glow { position:absolute; inset:-15px; border-radius:50%; background:radial-gradient(circle, rgba(212,160,23,0.15) 0%, transparent 70%); pointer-events:none; filter:blur(10px); }

      .db-hb-text { flex:1; position:relative; z-index:2; }
      .db-hb-welcome { display:flex; align-items:baseline; gap:10px; margin-bottom:4px; }
      .db-hb-eyebrow { font-family:'Cinzel',serif; font-size:9px; letter-spacing:4px; color:rgba(240,192,64,0.55); text-transform:uppercase; font-weight:700; }
      .db-hb-temple { font-family:'Cinzel Decorative',cursive; font-size:18px; font-weight:700; color:rgba(255,255,255,0.95); margin:0; }
      .db-hb-temple-name { font-family:'Cinzel Decorative',cursive; font-size:24px; font-weight:900; background:linear-gradient(135deg,#FFFDF0 0%,#F0C040 35%,#D4A017 65%,#B8860B 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; margin:2px 0 6px; }
      .db-hb-sub { font-family:'EB Garamond',serif; font-style:italic; font-size:14px; color:rgba(240,192,64,0.65); letter-spacing:0.5px; }

      .db-header-right { position:relative; z-index:2; margin-left:auto; display:flex; flex-direction:column; align-items:flex-end; gap:14px; }
      .db-updated-bar { font-family:'Cinzel',serif; font-size:9.5px; color:#9B6A3A; letter-spacing:1px; text-align:right; margin-top:4px; font-weight:600; opacity:0.7; }

      /* CLOCK */
      .db-clock { text-align:right; border-right:2px solid rgba(212,160,23,0.3); padding-right:14px; }
      .db-clock-time { font-family:'Cinzel Decorative',cursive; font-size:26px; font-weight:900; color:#F0C040; line-height:1; }
      .db-clock-sec { font-size:16px; opacity:0.6; }
      .db-clock-date { font-family:'EB Garamond',serif; font-style:italic; font-size:12.5px; color:rgba(240,192,64,0.6); margin-top:4px; letter-spacing:0.5px; }

      /* SYNC BTN */
      .db-refresh-banner-btn { display:inline-flex; align-items:center; gap:8px; font-family:'Cinzel',serif; font-size:10px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:#FFFDF0; background:linear-gradient(135deg,#8B0000,#5A1010); border:1.5px solid rgba(212,160,23,0.4); border-radius:8px; padding:10px 22px; cursor:pointer; box-shadow:0 2px 10px rgba(139,0,0,0.35); transition:all 0.25s cubic-bezier(0.4,0,0.2,1); }
      .db-refresh-banner-btn:hover:not(:disabled) { background:linear-gradient(135deg,#A93226,#8B0000); transform:translateY(-2px); box-shadow:0 6px 15px rgba(139,0,0,0.45); border-color:rgba(212,160,23,0.7); }
      .db-refresh-banner-btn:active { transform:translateY(0); }
      .db-refresh-banner-btn:disabled { opacity:0.6; cursor:not-allowed; }
      .db-refresh-banner-btn.spinning svg { animation:syncSpin 1s linear infinite; }
      @keyframes syncSpin { to { transform:rotate(360deg); } }

      /* ALERT */
      .db-alert-banner { display:flex; align-items:center; gap:12px; padding:12px 20px; border-radius:10px; background:rgba(212,160,23,0.1); border:1.5px solid rgba(212,160,23,0.4); cursor:pointer; font-family:'EB Garamond',serif; font-size:15px; color:#6B3A18; transition:all 0.2s; }
      .db-alert-banner:hover { background:rgba(212,160,23,0.18); }
      .db-alert-banner svg { color:#D4A017; flex-shrink:0; }
      .db-alert-action { margin-left:auto; font-family:'Cinzel',serif; font-size:9px; letter-spacing:1.5px; color:#D4A017; font-weight:700; }

      /* TODAY ROW */
      .db-today-row { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
      .db-today-card { display:flex; align-items:center; gap:14px; border-radius:12px; padding:16px 18px; border:1.5px solid rgba(212,160,23,0.2); background:var(--panel); box-shadow:0 2px 8px rgba(59,26,8,0.06); }
      .db-today-card svg { flex-shrink:0; }
      .db-today-card.gold svg { color:#D4A017; } .db-today-card.crimson svg { color:#8B0000; }
      .db-today-card.green svg { color:#1A5C2A; } .db-today-card.purple svg { color:#5C3A1E; }
      .db-today-val { font-family:'Cinzel Decorative',cursive; font-size:18px; font-weight:900; color:var(--text-dark); }
      .db-today-lbl { font-family:'Cinzel',serif; font-size:8.5px; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); margin-top:3px; }

      /* SECTION LABEL */
      .db-section-label { font-family:'Cinzel',serif; font-size:9px; font-weight:700; letter-spacing:3px; text-transform:uppercase; color:var(--text-muted); }

      /* QUICK ACTIONS */
      .db-qa-row { display:flex; gap:12px; flex-wrap:wrap; }
      .db-qa-btn { display:flex; flex-direction:column; align-items:center; gap:8px; padding:16px 20px; border-radius:12px; background:var(--panel); border:1.5px solid rgba(212,160,23,0.22); cursor:pointer; min-width:100px; transition:all 0.22s; box-shadow:0 2px 8px rgba(59,26,8,0.05); }
      .db-qa-btn:hover { border-color:var(--qa-color,#D4A017); background:rgba(212,160,23,0.07); transform:translateY(-3px); box-shadow:0 8px 24px rgba(59,26,8,0.12); }
      .db-qa-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:rgba(212,160,23,0.1); color:var(--qa-color,#D4A017); border:1.5px solid rgba(212,160,23,0.2); }
      .db-qa-label { font-family:'Cinzel',serif; font-size:8.5px; font-weight:700; letter-spacing:1px; text-transform:uppercase; color:var(--text-muted); text-align:center; white-space:nowrap; }

      /* STAT GRID */
      .db-stats-grid { display:grid; grid-template-columns:repeat(6,1fr); gap:14px; }
      .db-stat-card { position:relative; overflow:hidden; background:var(--panel); border:1.5px solid rgba(212,160,23,0.22); border-radius:14px; padding:18px 16px 14px; animation:scIn 0.55s ease both; transition:transform 0.22s,box-shadow 0.22s,border-color 0.22s; box-shadow:0 2px 8px rgba(59,26,8,0.06); }
      .db-stat-card::before { content:''; position:absolute; left:0; top:14px; bottom:14px; width:3px; border-radius:0 3px 3px 0; background:var(--sc-color,#D4A017); opacity:0.7; }
      .db-stat-card:hover { transform:translateY(-4px); box-shadow:0 8px 32px rgba(59,26,8,0.13); border-color:rgba(212,160,23,0.5); }
      @keyframes scIn { from { opacity:0; transform:scale(0.94) translateY(16px); } to { opacity:1; transform:none; } }
      .db-sc-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:12px; }
      .db-sc-icon-wrap { position:relative; width:38px; height:38px; border-radius:10px; background:rgba(212,160,23,0.1); border:1.5px solid rgba(212,160,23,0.22); display:flex; align-items:center; justify-content:center; color:var(--sc-color,#D4A017); }
      .db-sc-icon-ring { position:absolute; inset:-5px; border-radius:50%; border:1px dashed rgba(212,160,23,0.18); animation:slowSpin 14s linear infinite; }
      @keyframes slowSpin { to { transform:rotate(360deg); } }
      .db-sc-value { font-family:'Cinzel Decorative',cursive; font-size:19px; font-weight:900; color:var(--sc-color,#D4A017); margin-bottom:4px; }
      .db-sc-label { font-family:'Cinzel',serif; font-size:8.5px; font-weight:600; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); margin-bottom:6px; }
      .db-sc-sub { font-family:'Cinzel',serif; font-size:8.5px; letter-spacing:0.5px; display:flex; align-items:center; gap:3px; font-weight:600; }
      .db-sc-sub.pos { color:#1A5C2A; } .db-sc-sub.neg { color:#8B0000; }
      .db-sc-glow { position:absolute; bottom:-24px; right:-24px; width:80px; height:80px; border-radius:50%; background:radial-gradient(circle,var(--sc-color,#D4A017) 0%,transparent 70%); opacity:0.06; pointer-events:none; }

      /* PANEL BASE */
      .db-panel { background:var(--panel); border:1.5px solid rgba(212,160,23,0.22); border-radius:16px; overflow:hidden; position:relative; animation:panelIn 0.5s ease both; animation-delay:0.1s; box-shadow:0 2px 12px rgba(59,26,8,0.07); }
      .db-panel::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,transparent 10%,rgba(212,160,23,0.4) 40%,#D4A017 50%,rgba(212,160,23,0.4) 60%,transparent 90%); }
      @keyframes panelIn { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
      .db-panel-header { display:flex; align-items:center; gap:12px; padding:18px 22px 14px; }
      .db-panel-icon { width:34px; height:34px; border-radius:10px; background:rgba(139,0,0,0.1); border:1.5px solid rgba(139,0,0,0.18); display:flex; align-items:center; justify-content:center; color:#8B0000; flex-shrink:0; }
      .db-panel-title { font-family:'Cinzel',serif; font-size:12px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:var(--text-dark); }
      .db-panel-sub { font-family:'EB Garamond',serif; font-style:italic; font-size:12.5px; color:var(--text-muted); margin-top:2px; }
      .db-panel-rule { height:1px; margin:0 22px 18px; background:linear-gradient(90deg,transparent,rgba(212,160,23,0.4),transparent); position:relative; }
      .db-panel-rule::after { content:'✦'; position:absolute; left:50%; top:-7px; transform:translateX(-50%); font-size:9px; color:rgba(212,160,23,0.5); }

      /* MID GRID */
      .db-mid-grid { display:grid; grid-template-columns:1.55fr 1fr; gap:20px; }
      .db-bot-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:20px; }
      .db-chart-summary-row { display:flex; gap:20px; padding:0 22px 16px; flex-wrap:wrap; }
      .db-chart-sum-item { display:flex; align-items:flex-start; gap:10px; }
      .db-csi-dot { width:10px; height:10px; border-radius:3px; margin-top:4px; flex-shrink:0; }
      .db-csi-label { font-family:'Cinzel',serif; font-size:8px; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); margin-bottom:3px; }
      .db-csi-val { font-family:'Cinzel Decorative',cursive; font-size:15px; font-weight:700; }
      .db-bar-wrap { padding:0 22px 20px; }

      /* PULSE */
      .db-pulse-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; padding:0 22px 18px; }
      .db-pulse-item { display:flex; flex-direction:column; align-items:center; gap:5px; background:rgba(212,160,23,0.06); border:1.5px solid rgba(212,160,23,0.16); border-radius:12px; padding:13px 10px; }
      .db-pi-val { font-family:'Cinzel Decorative',cursive; font-size:22px; font-weight:900; }
      .db-pi-label { font-family:'Cinzel',serif; font-size:8px; letter-spacing:1px; text-transform:uppercase; color:var(--text-muted); text-align:center; }
      .db-is-wrap { padding:0 22px 20px; }
      .db-is-title { font-family:'Cinzel',serif; font-size:9px; letter-spacing:2px; text-transform:uppercase; color:var(--text-mid); margin-bottom:12px; font-weight:600; }
      .db-is-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
      .db-is-type { font-family:'Cinzel',serif; font-size:9.5px; color:var(--text-mid); width:68px; flex-shrink:0; font-weight:600; }
      .db-is-bar-wrap { flex:1; height:7px; background:rgba(59,26,8,0.07); border-radius:4px; overflow:hidden; }
      .db-is-bar { display:block; height:100%; border-radius:4px; transition:width 1.1s ease; }
      .db-is-amt { font-family:'Cinzel',serif; font-size:9.5px; color:var(--text-dark); font-weight:700; white-space:nowrap; min-width:65px; text-align:right; }

      /* PAYMENT MODE */
      .db-pmode { display:flex; flex-direction:column; gap:16px; }
      .db-pmode-bar { height:10px; border-radius:5px; background:rgba(59,26,8,0.08); display:flex; overflow:hidden; }
      .db-pmode-seg { height:100%; transition:width 1.1s ease; }
      .db-pmode-seg.cash { background:linear-gradient(90deg,#D4A017,#8B5E00); }
      .db-pmode-seg.online { background:linear-gradient(90deg,#1A5C2A,#27AE60); }
      .db-pmode-legend { display:flex; gap:24px; flex-wrap:wrap; }
      .db-pmode-item { display:flex; align-items:center; gap:10px; }
      .db-pmode-dot { width:12px; height:12px; border-radius:3px; flex-shrink:0; }
      .db-pmode-dot.cash { background:#D4A017; } .db-pmode-dot.online { background:#1A5C2A; }
      .db-pmode-lbl { font-family:'Cinzel',serif; font-size:9px; letter-spacing:1px; text-transform:uppercase; color:var(--text-muted); }
      .db-pmode-amt { font-family:'Cinzel Decorative',cursive; font-size:14px; color:var(--text-dark); }

      /* H-BARS */
      .db-hbars { padding:0 22px 20px; display:flex; flex-direction:column; gap:13px; }
      .db-hbar-row {}
      .db-hbar-top { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:5px; }
      .db-hbar-label { font-family:'EB Garamond',serif; font-size:14px; font-weight:500; color:var(--text-dark); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:62%; }
      .db-hbar-val { font-family:'Cinzel',serif; font-size:9px; color:var(--text-mid); display:flex; align-items:center; gap:5px; }
      .db-hbar-amt { font-weight:700; color:var(--text-dark); }
      .db-hbar-track { height:7px; background:rgba(59,26,8,0.08); border-radius:4px; overflow:hidden; }
      .db-hbar-fill { height:100%; border-radius:4px; transition:width 1.1s ease; }

      /* DONUT */
      .db-donut-wrap { display:flex; align-items:center; gap:20px; padding:0 22px 20px; }
      .db-donut-legend { display:flex; flex-direction:column; gap:8px; flex:1; }
      .db-dl-item { display:flex; align-items:center; gap:8px; }
      .db-dl-dot { width:10px; height:10px; border-radius:3px; flex-shrink:0; }
      .db-dl-name { font-family:'EB Garamond',serif; font-size:13.5px; color:var(--text-dark); flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .db-dl-val { font-family:'Cinzel',serif; font-size:9.5px; color:var(--text-mid); font-weight:700; }

      /* ACTIVITY */
      .db-activity-panel {}
      .db-activity-list { padding:0 22px 20px; display:flex; flex-direction:column; gap:2px; }
      .db-act-row { display:flex; align-items:center; gap:14px; padding:11px 14px; border-radius:10px; transition:background 0.18s; animation:actIn 0.4s ease both; }
      .db-act-row:hover { background:rgba(212,160,23,0.07); }
      @keyframes actIn { from { opacity:0; transform:translateX(-12px); } to { opacity:1; transform:none; } }
      .db-act-badge { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
      .db-act-badge.booking { background:rgba(212,160,23,0.15); color:#D4A017; border:1.5px solid rgba(212,160,23,0.3); }
      .db-act-badge.donation { background:rgba(192,74,0,0.13); color:#C04A00; border:1.5px solid rgba(192,74,0,0.3); }
      .db-act-info { flex:1; min-width:0; }
      .db-act-label { font-family:'EB Garamond',serif; font-size:14.5px; color:var(--text-dark); display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .db-act-ref { font-family:'Cinzel',serif; font-size:8px; color:var(--text-muted); letter-spacing:1px; }
      .db-act-right { display:flex; flex-direction:column; align-items:flex-end; gap:3px; }
      .db-act-amount { font-family:'Cinzel Decorative',cursive; font-size:14px; color:var(--text-dark); }
      .db-act-time { font-family:'EB Garamond',serif; font-style:italic; font-size:12px; color:var(--text-muted); }
      .db-empty-msg { padding:20px 22px; font-family:'EB Garamond',serif; font-style:italic; color:var(--text-muted); font-size:14px; }

      /* LOADER */
      .db-loader-wrap { min-height:80vh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:24px; background:var(--parchment); padding:120px 0; }
      .db-loader-ring-wrap { position:relative; width:80px; height:80px; display:flex; align-items:center; justify-content:center; background:rgba(201,151,58,0.05); border-radius:50%; box-shadow:inset 0 0 20px rgba(201,151,58,0.15); }
      .db-loader-ring { position:absolute; inset:-6px; border-radius:50%; border:1.5px dashed rgba(201,151,58,0.4); animation:dbSpin 6s linear infinite; }
      .db-loader-ring-inner { position:absolute; inset:4px; border-radius:50%; border:1px dotted rgba(201,151,58,0.6); animation:dbSpin 10s linear infinite reverse; }
      @keyframes dbSpin { to { transform:rotate(360deg); } }
      .db-loader-om { position:relative; z-index:1; font-family:'Cinzel Decorative',cursive; font-size:38px; background:linear-gradient(135deg,#fffaf0 0%,#e8c46a 40%,#c9973a 70%,#a06a15 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; animation:dbPulse 1.5s ease-in-out infinite alternate; filter:drop-shadow(0 2px 8px rgba(201,151,58,0.4)); }
      .db-loader-text { font-family:'EB Garamond',serif; font-style:italic; font-size:16px; color:rgba(74,26,8,0.5); letter-spacing:1px; animation:dbPulse 1.5s ease-in-out infinite alternate 0.5s; }
      @keyframes dbPulse { from { opacity:0.3; } to { opacity:1; } }

      /* ERROR */
      .db-error-wrap { min-height:80vh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; background:var(--parchment); }
      .db-error-msg { font-family:'EB Garamond',serif; font-style:italic; font-size:16px; color:rgba(139,0,0,0.7); }

      /* RESPONSIVE */
      @media (max-width:1200px) { 
        .db-stats-grid { grid-template-columns:repeat(3,1fr); } 
        .db-mid-grid { grid-template-columns:1fr; } 
        .db-bot-grid { grid-template-columns:1fr 1fr; } 
      }
      @media (max-width:820px) { 
        .db-root { padding:16px 12px 40px; } 
        .db-header-banner { padding:20px 24px; gap:16px; }
        .db-hb-rings { width:60px; height:60px; }
        .db-hb-ring { width:50px !important; height:50px !important; }
        .db-hb-ring:nth-child(2) { width:38px !important; height:38px !important; }
        .db-hb-ring:nth-child(3) { width:26px !important; height:26px !important; }
        .db-hb-om { font-size:24px; }
        .db-hb-temple-name { font-size:20px; }
        .db-hb-temple { font-size:15px; }
        .db-hb-sub { font-size:12px; }
        .db-stats-grid { grid-template-columns:repeat(2,1fr); gap:10px; } 
        .db-today-row { grid-template-columns:repeat(2,1fr); gap:10px; } 
        .db-qa-row { gap:8px; justify-content: center; } 
        .db-bot-grid { grid-template-columns:1fr; } 
      }
      @media (max-width:500px) { 
        .db-header-banner { flex-direction: column; text-align: center; gap: 12px; padding: 24px 16px; }
        .db-header-right { align-items: center; margin-left: 0; width: 100%; border-top: 1px solid rgba(212,160,23,0.2); padding-top: 15px; }
        .db-clock { text-align: center; border-right: none; padding-right: 0; margin-bottom: 5px; }
        .db-stats-grid { grid-template-columns:1fr; } 
        .db-today-row { grid-template-columns:1fr; } 
        .db-hb-temple { font-size:14px; } 
        .db-qa-btn { min-width: 90px; padding: 12px 10px; }
        .db-panel-header { padding: 14px 16px; }
        .db-bar-wrap, .db-hbars, .db-donut-wrap, .db-activity-list { padding: 0 16px 16px; }
      }
    `}</style>
  );
}